import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { DB_FIELD_MAP } from "../utils/applicationConstants";
import { invalidateCache } from "../../../utils/apiCache";

/**
 * Custom hook for application mutations (save, delete, archive, unarchive)
 * Handles optimistic updates and error rollback
 */
export const useApplicationMutations = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isSaving, setIsSaving] = useState({});

  /**
   * Invalidate server-side queries
   * Invalidates all variations of the ["applications", "query", ...] cache
   */
  const invalidateQueries = async () => {
    console.log('[invalidateQueries] Invalidating applications queries...');
    await queryClient.invalidateQueries({
      queryKey: ["applications", "query"],
      refetchType: 'active',
    });
    await queryClient.invalidateQueries({
      queryKey: ["applications"],
      refetchType: 'active',
    });
    invalidateCache("/api/applications/");
    console.log('[invalidateQueries] Invalidation complete');
  };

  /**
   * Update React Query cache optimistically
   * Used for inline edits to provide instant UI feedback
   * @param {Function} updater - Function that receives current data and returns updated data
   */
  const updateCache = (updater) => {
    queryClient.setQueriesData(
      { queryKey: ["applications", "query"], exact: false },
      (oldData) => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: updater(oldData.data),
        };
      }
    );
  };

  /**
   * Save application row changes
   * @param {string} id - Application ID
   * @param {Object} changes - Changes object {field: {original, current}}
   * @returns {Promise<boolean>} Success status
   */
  const saveApplication = async (id, changes) => {
    if (!changes) return false;

    setIsSaving((prev) => ({ ...prev, [id]: true }));
    const token = localStorage.getItem("token");
    let success = true;

    try {
      for (const [jsField, { current }] of Object.entries(changes)) {
        const dbField = DB_FIELD_MAP[jsField];
        if (!dbField) continue;

        const res = await fetch("/api/applications/update", {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Ids: [id],
            Changes: { [dbField]: current },
          }),
        });

        if (!res.ok) {
          throw new Error((await res.json()).message || res.statusText);
        }
      }

      toast({
        title: "Saved",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      // Invalidate all paginated queries to refetch with new data
      invalidateQueries();
    } catch (error) {
      toast({
        title: "Save failed: " + error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });

      // Server-side query will refetch on next interaction
      success = false;
    } finally {
      setIsSaving((prev) => ({ ...prev, [id]: false }));
    }

    return success;
  };

  /**
   * Delete applications
   * @param {Array} applications - Current applications array
   * @param {Array} selectedIds - IDs to delete
   * @returns {Promise<boolean>} Success status
   */
  const deleteApplications = async (applications, selectedIds) => {
    if (!selectedIds.length) return false;

    const token = localStorage.getItem("token");

    console.log('[deleteApplications] Starting delete for IDs:', selectedIds);

    try {
      // First, do the API call
      const res = await fetch("/api/applications/delete", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(selectedIds),
      });

      if (!res.ok) {
        throw new Error((await res.json()).message || res.statusText);
      }

      toast({
        title: "Deleted",
        status: "success",
        duration: 1000,
      });

      // Then invalidate to refetch with updated data
      await invalidateQueries();

      return true;
    } catch (error) {
      toast({
        title: "Delete failed",
        status: "error",
        duration: 1000,
      });
      return false;
    }
  };

  /**
   * Archive applications
   * @param {Array} applications - Current applications array
   * @param {Array} selectedIds - IDs to archive
   * @returns {Promise<boolean>} Success status
   */
  const archiveApplications = async (applications, selectedIds) => {
    if (!selectedIds.length) return false;

    const token = localStorage.getItem("token");

    // Optimistically update cache immediately
    queryClient.setQueriesData(
      { queryKey: ["applications", "query"] },
      (oldData) => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(app => 
            selectedIds.includes(app.id) ? { ...app, bArchive: true } : app
          ),
        };
      }
    );

    try {
      const res = await fetch("/api/applications/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Ids: selectedIds,
          Changes: { barchive: true },
        }),
      });

      if (!res.ok) {
        throw new Error((await res.json()).message || res.statusText);
      }

      toast({
        title: `${selectedIds.length} application(s) archived`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // Invalidate queries to refetch
      invalidateQueries();

      return true;
    } catch (error) {
      // Rollback on error
      invalidateQueries();

      toast({
        title: "Archive failed",
        status: "error",
        duration: 1000,
      });
      return false;
    }
  };

  /**
   * Unarchive applications
   * @param {Array} applications - Current applications array
   * @param {Array} selectedIds - IDs to unarchive
   * @returns {Promise<boolean>} Success status
   */
  const unarchiveApplications = async (applications, selectedIds) => {
    if (!selectedIds.length) return false;

    const token = localStorage.getItem("token");

    // Optimistically update cache immediately
    queryClient.setQueriesData(
      { queryKey: ["applications", "query"] },
      (oldData) => {
        if (!oldData?.data) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(app => 
            selectedIds.includes(app.id) ? { ...app, bArchive: false } : app
          ),
        };
      }
    );

    try {
      const res = await fetch("/api/applications/update", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          Ids: selectedIds,
          Changes: { barchive: false },
        }),
      });

      if (!res.ok) {
        throw new Error((await res.json()).message || res.statusText);
      }

      toast({
        title: "Unarchived",
        status: "success",
        duration: 1000,
      });

      // Invalidate queries to refetch
      invalidateQueries();

      return true;
    } catch (error) {
      // Rollback on error
      invalidateQueries();
      
      toast({
        title: "Unarchive failed",
        status: "error",
        duration: 1000,
      });
      return false;
    }
  };

  return {
    saveApplication,
    deleteApplications,
    archiveApplications,
    unarchiveApplications,
    updateCache,
    isSaving,
  };
};
